import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
	PrimaryGeneratedColumn,
} from 'typeorm';
import { AutomationStepExecutionEntity } from './automation-step-execution.entity';
import { BaseEntity } from './base.entity';
import { EmailAttachmentEntity } from './email-attachment.entity';
import { EmailEventEntity } from './email-event.entity';
import { EmailRecipientEntity } from './email-recipient.entity';
import { EmailRecordFolderEntity } from './email-record-folder.entity';
import { EmailSourceEntity } from './email-source.entity';
import { EmailUserStatusEntity } from './email-user-status.entity';

@Entity('email_record')
@Index(['calendarStartDate', 'calendarEndDate'])
@Index(['emailSourceId', 'externalId'])
@Index(['emailSourceId', 'isDeleted'])
export class EmailRecordEntity extends BaseEntity<string> {
	@PrimaryGeneratedColumn({ name: '_id', type: 'bigint', unsigned: true })
	declare id: string;

	// Source tracking fields
	@Column({
		name: 'source_type',
		type: 'enum',
		enum: ['provider_sync', 'system_send', 'user_send'],
		default: "'provider_sync'",
	})
	public sourceType!: 'provider_sync' | 'system_send' | 'user_send';

	@Column({
		name: 'source_provider',
		type: 'varchar',
		length: 50,
		nullable: true,
	})
	public sourceProvider?: string; // 'o365', 'gmail', 'aws-ses', 'sendgrid'

	// Provider sync fields (nullable for system sends)
	@Column({
		name: 'fk_email_source__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public emailSourceId?: number;

	@ManyToOne(() => EmailSourceEntity, (source) => source.records, {
		nullable: true,
	})
	@JoinColumn({ name: 'fk_email_source__id' })
	public emailSource?: EmailSourceEntity;

	@Column({
		name: 'external_id',
		type: 'varchar',
		length: 255,
		nullable: true,
		charset: 'utf8mb4',
		collation: 'utf8mb4_0900_as_cs',
	})
	public externalId?: string;

	@Column({
		name: 'immutable_id',
		type: 'varchar',
		length: 255,
		nullable: true,
		charset: 'utf8mb4',
		collation: 'utf8mb4_0900_as_cs',
	})
	public immutableId?: string;

	@Column({
		name: 'message_id',
		type: 'varchar',
		length: 255,
		charset: 'utf8mb4',
		collation: 'utf8mb4_0900_as_cs',
	})
	@Index()
	public messageId!: string;

	@Column({ type: 'varchar', length: 255 })
	public subject!: string;

	@Column({ type: 'timestamp', nullable: true })
	public date?: Date;

	@Column({ type: 'varchar', length: 150 })
	public type!: string; // 'email', 'event', etc.

	@Column({ type: 'varchar', length: 255, nullable: true })
	public preview?: string;

	@Column({ type: 'longtext', nullable: true })
	public content?: string;

	@Column({
		name: 'thread_identifier',
		type: 'varchar',
		length: 255,
		nullable: true,
	})
	@Index()
	public threadIdentifier?: string;

	@Column({ name: 'calendar_start_date', type: 'timestamp', nullable: true })
	public calendarStartDate?: Date;

	@Column({ name: 'calendar_end_date', type: 'timestamp', nullable: true })
	public calendarEndDate?: Date;

	@Column({ name: 'is_deleted', type: 'boolean', default: false })
	public isDeleted!: boolean;

	@Column({ name: 'from_email', type: 'varchar', length: 320, nullable: true })
	public fromEmail?: string;

	@Column({ name: 'from_name', type: 'varchar', length: 255, nullable: true })
	public fromName?: string;

	@Column({ name: 'is_read', type: 'boolean', default: false })
	public isRead!: boolean;

	@Column({ name: 'is_starred', type: 'boolean', default: false })
	public isStarred!: boolean;

	@Column({ name: 'has_attachments', type: 'boolean', default: false })
	public hasAttachments!: boolean;

	@Column({ name: 'body_type', type: 'varchar', length: 10, default: "'text'" })
	public bodyType!: 'text' | 'html';

	// System send specific fields
	@Column({
		name: 'fk_email_template__id',
		type: 'int',
		nullable: true,
		unsigned: true,
	})
	public emailTemplateId?: number;

	@Column({
		name: 'fk__automation_step_execution__id',
		type: 'int',
		nullable: true,
		unsigned: true,
	})
	public automationStepExecutionId?: number;

	@ManyToOne(() => AutomationStepExecutionEntity, { nullable: true })
	@JoinColumn({ name: 'fk__automation_step_execution__id' })
	public automationStepExecution?: AutomationStepExecutionEntity;

	@Column({ name: 'tracking_enabled', type: 'boolean', default: false })
	public trackingEnabled!: boolean;

	@Column({
		name: 'send_status',
		type: 'enum',
		enum: ['pending', 'sent', 'failed', 'bounced', 'complained'],
		nullable: true,
	})
	public sendStatus?: 'pending' | 'sent' | 'failed' | 'bounced' | 'complained';

	@Column({ name: 'sent_at', type: 'timestamp', nullable: true })
	public sentAt?: Date;

	@Column({ name: 'failed_at', type: 'timestamp', nullable: true })
	public failedAt?: Date;

	@Column({ name: 'error_message', type: 'text', nullable: true })
	public errorMessage?: string;

	@Column({ name: 'metadata', type: 'json', nullable: true })
	public metadata?: Record<string, any>;

	@OneToMany(() => EmailRecordFolderEntity, (link) => link.emailRecord)
	public folderLinks!: EmailRecordFolderEntity[];

	@OneToMany(() => EmailRecipientEntity, (recipient) => recipient.emailRecord)
	public recipients!: EmailRecipientEntity[];

	@OneToMany(() => EmailUserStatusEntity, (status) => status.emailRecord)
	public userStatuses!: EmailUserStatusEntity[];

	@OneToMany(
		() => EmailAttachmentEntity,
		(attachment) => attachment.emailRecord,
	)
	public attachments!: EmailAttachmentEntity[];

	@OneToMany(() => EmailEventEntity, (event) => event.emailRecord)
	public events?: EmailEventEntity[];
}
